// client/src/pages/PaymentMockPage.js
// Stands in for PayGate's hosted PayWeb3 payment page while PAYMENT_MODE=test
// on the server. Once real PayGate credentials are available, this route can
// be replaced by an auto-submitting form that POSTs to PayGate's actual
// process.trans URL — nothing else in the checkout flow needs to change.
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, CardContent, Typography, Button, Stack, Alert, CircularProgress, Divider } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import PaymentIcon from '@mui/icons-material/Payment';
import { AuthContext } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import { PAYMENT_METHODS } from '../constants/paymentMethods';
import { API_BASE_URL } from '../utils/media';

const METHOD_ICONS = { CreditCard: CreditCardIcon, Apple: AppleIcon, Google: GoogleIcon, Payment: PaymentIcon };

const getMethodInfo = (method) => {
  const found = PAYMENT_METHODS.find((m) => m.value === method) || PAYMENT_METHODS[0];
  return { ...found, Icon: METHOD_ICONS[found.icon] || LockIcon };
};

const PaymentMockPage = () => {
  const { payRequestId } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchPayment = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/${payRequestId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Payment not found.');
      setPayment(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [payRequestId, token]);

  useEffect(() => { fetchPayment(); }, [fetchPayment]);

  const handleOutcome = async (outcome) => {
    setProcessing(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/${payRequestId}/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ outcome }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to process payment.');
      navigate(`/orders/${data.orderId}?payment=${outcome === 'success' ? 'success' : 'failed'}`);
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
  };

  if (loading) return <Container sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Container>;

  const { label, Icon } = getMethodInfo(payment?.method);

  return (
    <Container maxWidth="xs" sx={{ py: 8 }}>
      <Alert severity="warning" sx={{ mb: 2 }}>
        TEST MODE — this stands in for {label === 'Card' ? "PayGate's hosted payment page" : `${label}'s checkout flow`}. No real payment details are collected and no money moves.
      </Alert>
      <Card variant="outlined">
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Icon color="action" sx={{ fontSize: 32, mb: 1 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {label === 'Card' ? 'PayGate PayWeb3 (Sandbox)' : `${label} (Sandbox)`}
          </Typography>

          {error && <Alert severity="error" sx={{ my: 2, textAlign: 'left' }}>{error}</Alert>}

          {payment && (
            <>
              <Typography color="text.secondary" sx={{ mt: 1 }}>Amount Due</Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                {formatCurrency(payment.amount)}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Stack spacing={1.5}>
                <Button
                  variant="contained" color="success" size="large"
                  disabled={processing} onClick={() => handleOutcome('success')}
                >
                  {processing ? 'Processing...' : 'Simulate Successful Payment'}
                </Button>
                <Button
                  variant="outlined" color="error" size="large"
                  disabled={processing} onClick={() => handleOutcome('failure')}
                >
                  Simulate Failed Payment
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentMockPage;
